import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import locationService from '../../services/LocationService.js';
import { MapPin, Plus, Filter, ArrowLeft, Eye, Trash, Pencil } from "lucide-react";

const LocationList = () => {
  const [locations, setLocations] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stateFilter, setStateFilter] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchLocations();
    }, 300);

    return () => clearTimeout(timer);
  }, [search, stateFilter]);

  useEffect(() => {
    if (currentPage !== 1) {
      fetchLocations();
    }
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchStates = async () => {
    try {
      const response = await locationService.getStates();
      setStates(response.data || []);
    } catch (err) {
      console.error('Failed to load states:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (stateFilter) filters.state = stateFilter;

      const response = await locationService.getAll(currentPage, perPage, filters);
      setLocations(response.data || []);
      
      if (response.pagination) {
        setTotalPages(response.pagination.pages);
      } else {
        setTotalPages(1);
      }
    } catch (err) {
      Alert.error('Failed to load locations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (locationId, locationName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to permanently delete location "${locationName}"? This action cannot be undone.`,
      'Delete Location'
    );
    
    if (confirmed) {
      try {
        await locationService.delete(locationId);
        Alert.success('Location deleted successfully!');
        
        if (locations.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchLocations();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete location');
      }
    }
  };

  const toggleDropdown = (locationId) => {
    setActiveDropdown(activeDropdown === locationId ? null : locationId);
  };

  const clearFilters = () => {
    setSearch('');
    setStateFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || stateFilter;

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex h-screen bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <MapPin className="w-5 h-5 text-buttonBg" />
                      Locations
                    </h1>
                  </div>
                  <button
                    onClick={() => navigate('/locations/add')}
                    className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON */}
                          <Plus
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />

                          <span className="relative z-10">Add Location</span>
                  </button>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 sm:p-6 rounded-sm shadow-sm mb-4 sm:mb-6">
                  <div className="flex items-center justify-between mb-0 sm:mb-4">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 sm:w-4 sm:h-4 text-buttonBg font-bold"/>
                      <h3 className="text-base sm:text-lg font-semibold text-tertiary">Filters</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowMobileFilters(!showMobileFilters)}
                        className="sm:hidden text-orange-500 font-medium text-sm"
                      >
                        {showMobileFilters ? 'Hide' : 'Show'} Filters
                      </button>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors"
                          title="Clear all filters"
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-5 w-5" 
                            viewBox="0 0 20 20" 
                            fill="currentColor"
                          >
                            <path 
                              fillRule="evenodd" 
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" 
                              clipRule="evenodd" 
                            />
                          </svg>
                          <span className="hidden sm:inline text-sm font-medium">Clear</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={`${showMobileFilters ? 'block' : 'hidden'} sm:block`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">Search</label>
                        <input
                          type="text"
                          placeholder="City, State, City Code..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">State</label>
                        <select
                          value={stateFilter}
                          onChange={(e) => setStateFilter(e.target.value)}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-sm font-medium text-tertiary"
                        >
                          <option value="">All States</option>
                          {states.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table - Desktop View */}
                <div className="bg-white rounded-sm shadow-sm">
                  {loading ? (
                    <div className="p-8 text-center text-gray-600">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                      Loading locations...
                    </div>
                  ) : locations.length === 0 ? (
                    <div className="p-8 text-center text-gray-600">No locations found</div>
                  ) : (
                    <>
                      <div className="hidden md:block w-full">
                        <table className="w-full table-fixed border-collapse">
                          <thead>
                            <tr className="bg-primary text-tertiary">
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">CITY</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">STATE</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">CITY CODE</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">CREATED BY</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">CREATED AT</th>
                              <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">ACTIONS</th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-200">
                            {locations.map((location) => (
                              <tr key={location.id} className="hover:bg-gray-50 transition-colors relative">
                                <td className="px-4 py-3">
                                  <button
                                    onClick={() => navigate(`/locations/view/${location.id}`)}
                                    className="font-medium text-tertiary hover:text-tertiary hover:underline"
                                  >
                                    {location.city}
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-gray-700">{location.state}</td>
                                <td className="px-4 py-3">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    {location.city_code}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="relative group w-fit">
                                    <span className="truncate max-w-[80px] inline-block">
                                      {location.created_by || 'N/A'}
                                    </span>
                                    {location.created_by && (
                                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2
                                                      hidden group-hover:block
                                                      bg-gray-800 text-white text-xs rounded px-2 py-1
                                                      whitespace-nowrap z-50 shadow-lg">
                                        {location.created_by}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                  {new Date(location.created_at).toLocaleDateString('en-IN')}
                                </td>
                                <td className="px-4 py-3 relative">
                                  <button
                                    onClick={() => toggleDropdown(location.id)}
                                    className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                                    title="Actions"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-5 w-5 text-gray-600"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle cx="12" cy="5" r="2"/>
                                      <circle cx="12" cy="12" r="2"/>
                                      <circle cx="12" cy="19" r="2"/>
                                    </svg>
                                  </button>

                                  {activeDropdown === location.id && (
                                    <div
                                      ref={dropdownRef}
                                      className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10"
                                    >
                                      <button
                                        onClick={() => {
                                          navigate(`/locations/view/${location.id}`);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center border-b"
                                      >
                                        <Eye size={16} className="text-buttonBg" />
                                        <span className='text-base capitalize'>View Details</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          navigate(`/locations/edit/${location.id}`);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center border-b"
                                      >
                                       <Pencil size={16} className="text-buttonBg" />
                                        <span className='text-base capitalize'>Edit</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleDelete(location.id, `${location.city}, ${location.state}`);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center"
                                      >
                                        <Trash size={16} className="text-buttonBg" />
                                        <span className='text-base capitalize'>Delete</span>
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      <div className="hidden md:flex bg-gray-50 px-6 py-4 justify-between items-center border-t">
                        <div className="text-sm text-gray-700">
                          Showing {locations.length} location{locations.length !== 1 ? 's' : ''}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                          >
                            ← Previous
                          </button>
                          <span className="px-4 py-2 text-sm text-gray-700">
                            Page {currentPage} of {totalPages}
                          </span>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Card View - Mobile/Tablet */}
                <div className="lg:hidden space-y-3 sm:space-y-4">
                  {loading ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                      Loading locations...
                    </div>
                  ) : locations.length === 0 ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">No locations found</div>
                  ) : (
                    <>
                      {locations.map((location) => (
                        <div key={location.id} className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                          <div className="flex items-start gap-3 sm:gap-4 mb-3">
                            <MapPin className="w-10 h-10 text-tertiary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">
                                {location.city}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-600 truncate">
                                {location.state}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3">
                            <div>
                              <span className="text-gray-500">City Code:</span>
                              <p className="text-gray-900 font-medium">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  {location.city_code}
                                </span>
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500">Created:</span>
                              <p className="text-gray-900 font-medium">
                                {new Date(location.created_at).toLocaleDateString('en-IN')}
                              </p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => navigate(`/locations/view/${location.id}`)}
                              className="flex-1 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs sm:text-sm font-medium"
                            >
                              👁️ View
                            </button>
                            <button
                              onClick={() => navigate(`/locations/edit/${location.id}`)}
                              className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDelete(location.id, `${location.city}, ${location.state}`)}
                              className="px-3 sm:px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs sm:text-sm font-medium"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Mobile Pagination */}
                      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                        <div className="text-xs sm:text-sm text-gray-700 text-center mb-3">
                          Showing {locations.length} location{locations.length !== 1 ? 's' : ''} - Page {currentPage} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                          >
                            ← Previous
                          </button>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationList;